import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import {
  useHasPolicyProductConfig,
  usePolicyProductConfig,
} from '@folio/utils/usePolicyProductConfig';
import { useKorintProductConfigSuspense } from 'korint-sdk/react';
import { Alert, AlertTitle } from '../../components/ui/alert';
import { buttonVariants } from '../../components/ui/button';
import { AlertCircleIcon, AlertOctagonIcon, ChevronRightIcon, ClockIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useWakamDashboardError } from './useWakamDashboardError';
import { WakamErrorBanner } from './WakamErrorBanner';
import { branchDtoBranchStatusEnum } from 'korint-sdk';
import { CONTINUABLE_IN_QUOTE_FUNNEL } from './continuable-branches';
import { ScheduledStopAlert } from './ScheduledStopAlert';
import { StopDraftAlert } from './StopDraftAlert';
import { Stack } from '@folio/components/ui/flex';

export function PolicyWarningBanner({
  disableQuoteAction,
  stopFunnelBasePath,
}: {
  disableQuoteAction?: boolean;
  stopFunnelBasePath?: string;
} = {}) {
  const { t } = useTranslation();
  const { policy, workingBranch } = usePolicyOnDashboard();

  const productCustomPath = usePolicyProductConfig('productCustomPath');
  const isWakamErrorEnabled = useHasPolicyProductConfig('isWakamErrorEnabled');
  const wakamError = useWakamDashboardError({ enabled: isWakamErrorEnabled });

  const productsConfig = useKorintProductConfigSuspense();
  const customBanners = productsConfig[policy.productId]?.policyWarningBanners ?? [];

  return (
    <Stack className="empty:hidden">
      <ScheduledStopAlert className="bg-card" />
      <StopDraftAlert className="bg-card" funnelBasePath={stopFunnelBasePath} />
      {policy.status === 'POLICY_STOPPED' && (
        <Alert variant="destructive" className="bg-card">
          <AlertTitle className="flex items-center gap-4 pt-1">
            <AlertOctagonIcon />
            {t('warningBanner.policyStopped', {
              reason: policy.stop?.reason
                ? t([
                    `${policy.productId}.stopReasons.${policy.stop.reason}`,
                    `stopReasons.${policy.stop.reason}`,
                  ])
                : '',
            })}
          </AlertTitle>
        </Alert>
      )}
      {policy.isAwaitingFirstPayment && (
        <Alert variant="info" className="bg-card">
          <AlertTitle className="flex items-center gap-4 pt-1">
            <ClockIcon />
            {t('warningBanner.awaitingFirstPayment')}
          </AlertTitle>
        </Alert>
      )}
      <WakamErrorBanner errorMessage={wakamError} />
      {customBanners.map((CustomBanner, index) => (
        <CustomBanner key={index} />
      ))}
      {!disableQuoteAction &&
        (policy.status === 'POLICY_CREATED' || policy.status === 'POLICY_QUOTED') &&
        workingBranch?.branchStatus === branchDtoBranchStatusEnum.BRANCH_OPEN &&
        // Only the quote funnel lives at /discovery; a STOP branch has its own, and no other
        // branch type has one — sending them here lands the user in the wrong funnel.
        CONTINUABLE_IN_QUOTE_FUNNEL.includes(workingBranch.branchType ?? '') && (
          <Alert variant="info" className="flex justify-between items-center">
            <AlertTitle className="flex items-center gap-4 pt-1">
              <AlertCircleIcon />
              {t('warningBanner.quoteInProgress')}
            </AlertTitle>
            <Link
              to={`/discovery${productCustomPath}/${policy.customers[0]}/${policy.id}/${workingBranch?.id}`}
              className={buttonVariants()}
            >
              {t(
                policy.status === 'POLICY_QUOTED'
                  ? 'warningBanner.editQuote'
                  : 'warningBanner.continueQuote',
              )}{' '}
              <ChevronRightIcon className="h-4 w-4 ml-2" />
            </Link>
          </Alert>
        )}
    </Stack>
  );
}
